import React, { createContext, useContext, useEffect, useState } from "react";

const translations = {
  en: {
    nav: {
      home: "Home",
      about: "About",
      services: "Services",
      portfolio: "Portfolio",
      process: "Process",
      contact: "Contact",
      cta: "Request Info",
    },
    hero: {
      ornament: "Football · Scouting · Media",
      title: "Professional Football Highlight Videos & Player Analysis",
      subtitle:
        "Turn your match footage into clean, professional material that helps you present your game to clubs, coaches and agents.",
      cta_view: "View Work",
      cta_contact: "Contact Us",
      scroll: "Scroll",
      pillars: [
        { title: "Highlight Videos", sub: "Match clips into pro reels" },
        { title: "Player Analysis", sub: "Scouting-style breakdown" },
        { title: "Promotion", sub: "Material ready for scouts" },
      ],
    },
    about: {
      kicker: "About",
      title_1: "Built for Players.",
      title_2: "Trusted by",
      title_3: "Scouts.",
      p1: "PlayerSpotlight is created by Giannis Chatzikyriakos, a football analyst based in Greece. The goal is to help footballers present their game through professional highlight videos and individual player analysis.",
      p2: "We combine clean editing, scouting-style analysis, and a presentation that helps every player tell the story their performances deserve — whether you're competing in an academy, semi-pro league, or chasing your next club.",
      f1: "Scouting-grade presentation",
      f2: "Pro editing workflow",
      f3: "Performance-focused analysis",
      f4: "Built for football, only",
    },
    services: {
      kicker: "— Services",
      title: "What We Do",
      subtitle:
        "Two focused services. One mission: get your football story in front of the right people.",
      price_label: "Contact for price",
      cta: "Request Info",
      items: [
        {
          title: "Highlight Video",
          desc: "Professional football highlight video created from match footage, focused on the player's best actions, key moments and strongest qualities.",
          bullets: [
            "Best actions & key moments",
            "Slow-motion on decisive plays",
            "Clean cuts, branded intro",
          ],
        },
        {
          title: "Player Analysis",
          desc: "Detailed individual player analysis based on match footage. Accompanied by a written report and an individual video analysis — strengths, key actions, tactical/technical points and areas for improvement.",
          bullets: [
            "Written scouting-style report",
            "Individual video breakdown",
            "Strengths, tactics & areas to improve",
          ],
        },
      ],
    },
    portfolio: {
      kicker: "— Recent Work",
      title: "Recent Work",
      subtitle:
        "Highlight videos and player analysis produced for footballers across positions and levels.",
      cta: "Visit YouTube Channel",
      watch: "Watch",
      placeholder: "Watch on YouTube",
      labels: { position: "Position", service: "Service" },
      videos: [
        { id: "5UumdwhJ_Cg", player: "Giannis Ntokos", position: "Right Back / Right Wing-Back", service: "Highlight Video" },
        { id: "VnF3Tn7YGpo", player: "Konstantinos Tsaggaris", position: "Striker / Winger / Attacking Midfielder", service: "Highlight Video" },
        { id: "iz7xJ_20uSk", player: "Theodosis Prekas", position: "Defensive Midfielder / Attacking Midfielder", service: "Highlight Video" },
        { id: "EAvPkmncUjA", player: "Alexandros Charalampidis", position: "Attacking Midfielder / Winger", service: "Highlight Video" },
        { id: "qjY7zyI0XvQ", player: "Vasilis Patsatzoglou", position: "Center Back", service: "Highlight Video" },
        { id: "yzTBX1d1L3w", player: "Paris Prikas", position: "Left Back / Left Wing-Back", service: "Highlight Video" },
        { id: "KnEFD4oyJrA", player: "Giannis Farmakis", position: "Center Back / Defensive Midfielder", service: "Highlight Video" },
        { id: "3a5UPuNh7ig", player: "Aggelos Efthimiou", position: "Right-Back / Left-Back", service: "Highlight Video" },
        { id: "H-nMOelFgvY", player: "Achilleas Pantelis", position: "Left-Back / Left Wing-Back / Left Wing", service: "Highlight Video" },
        { id: "T3tzef-nT3w", player: "Salvatore Torma", position: "Center-Back", service: "Highlight Video" },
        { id: "P5BNAt9fm_w", player: "Marios Ventouris", position: "Center Back", service: "Highlight Video" },
        { id: "f2gXsi0FFO8", player: "David Krupička", position: "Right Back / Left Back", service: "Highlight Video" },
        { id: "4dtvAs9JFKI", player: "Luka Mamed-Ogly", position: "Left Winger", service: "Highlight Video" },
        { id: "aPrS49Mh1Ck", player: "Konstantinos Kaipis", position: "Right-Back / Left-Back", service: "Highlight Video" },
      ],
    },
    why: {
      kicker: "— Who It's For",
      title_1: "Who It's For",
      brand_a: "Player",
      brand_b: "Spotlight",
      subtitle:
        "Built for the two people who decide what happens next — the player and the agent.",
      audiences: [
        {
          tag: "For Players",
          title: "Present Your Game Like A Pro",
          desc: "Show clubs, coaches and scouts what you can really do — with material that holds up next to the biggest academies.",
          bullets: [
            "Professional highlight videos that get noticed",
            "Scouting-style analysis of your strengths and game",
            "Material ready to send to clubs, scouts and agents",
            "Stand out from thousands of unedited highlights",
          ],
        },
        {
          tag: "For Agents",
          title: "Equip Every Client You Represent",
          desc: "Save hours of editing and deliver presentations that match what clubs actually expect to see.",
          bullets: [
            "Ready-to-share material for every player on your roster",
            "Reports formatted the way decision-makers read them",
            "Fast turnaround for time-sensitive transfer windows",
            "Consistent quality across all your clients",
          ],
        },
      ],
    },
    process: {
      kicker: "— Process",
      title: "How It Works",
      subtitle: "A simple, four-step workflow — from your footage to material you can send anywhere.",
      steps: [
        { title: "Send Your Match Footage", desc: "Share your match videos via Drive, WeTransfer, or YouTube link — full matches or clips." },
        { title: "We Select & Analyse The Key Actions", desc: "We watch every minute and identify the moments that show your strengths." },
        { title: "We Create The Highlight Video Or Player Analysis", desc: "Professional editing or scouting-style analysis tailored to your position." },
        { title: "You Receive Ready-To-Share Material", desc: "Delivered in formats ready for clubs, coaches, scouts, and social media." },
      ],
    },
    contact: {
      kicker: "— Contact",
      title_1: "Request",
      title_2: "Information",
      subtitle:
        "Tell us what you are interested in and we will contact you with more details and pricing.",
      labels: {
        instagram: "Instagram",
        email: "Email",
        youtube: "YouTube",
        location: "Location",
      },
      location_value: "Worldwide · Remote",
      form: {
        name: "Name",
        name_ph: "Your full name",
        email: "Email",
        email_ph: "you@email.com",
        contact_handle: "Instagram / WhatsApp",
        contact_handle_ph: "@your_instagram or WhatsApp number",
        position: "Player Position",
        position_ph: "e.g. Central Midfielder, Right Back, Goalkeeper",
        interest: "What are you interested in?",
        interest_options: {
          highlight: "Highlight Video",
          analysis: "Player Analysis",
          both: "Both",
        },
        message: "Message / Footage link",
        message_ph:
          "Tell us about yourself and share a link to your footage (Drive, WeTransfer, YouTube).",
        submit: "Request Information",
        submitting: "Sending…",
        success: "Request sent! We'll be in touch with details and pricing.",
        error_required: "Please fill in name, email, what you're interested in, and a message.",
        error_generic: "Could not send your request.",
      },
    },
    footer: {
      tagline: "Professional Football Highlight Videos & Player Analysis",
      rights: "All Rights Reserved",
      nav: { about: "About", services: "Services", portfolio: "Portfolio", contact: "Contact" },
    },
    lang_label: "Language",
  },
  el: {
    nav: {
      home: "Αρχική",
      about: "Σχετικά",
      services: "Υπηρεσίες",
      portfolio: "Έργα",
      process: "Διαδικασία",
      contact: "Επικοινωνία",
      cta: "Πληροφορίες",
    },
    hero: {
      ornament: "Ποδόσφαιρο · Scouting · Παραγωγή",
      title: "Επαγγελματικά Highlight Videos & Player Analysis",
      subtitle:
        "Μετέτρεψε το υλικό των αγώνων σου σε καθαρό, επαγγελματικό υλικό που σε βοηθά να παρουσιάσεις το παιχνίδι σου σε συλλόγους, προπονητές και ατζέντηδες.",
      cta_view: "Δες τα Έργα",
      cta_contact: "Επικοινωνία",
      scroll: "Κύλιση",
      pillars: [
        { title: "Highlight Videos", sub: "Πλάνα αγώνων σε επαγγελματικά reels" },
        { title: "Player Analysis", sub: "Ανάλυση τύπου scouting" },
        { title: "Promotion", sub: "Υλικό έτοιμο για scouts" },
      ],
    },
    about: {
      kicker: "Σχετικά",
      title_1: "Φτιαγμένο για Παίκτες.",
      title_2: "Έμπιστο από",
      title_3: "Scouts.",
      p1: "Η PlayerSpotlight δημιουργήθηκε από τον Γιάννη Χατζηκυριάκο, ποδοσφαιρικό αναλυτή από την Ελλάδα. Στόχος μας είναι να βοηθάμε ποδοσφαιριστές να παρουσιάσουν το παιχνίδι τους μέσα από επαγγελματικά highlight videos και ατομική ανάλυση παίκτη.",
      p2: "Συνδυάζουμε καθαρό μοντάζ, ανάλυση scouting και μια παρουσίαση που βοηθά κάθε παίκτη να αφηγηθεί την ιστορία που του αξίζει — είτε αγωνίζεται σε ακαδημία, ημι-επαγγελματική κατηγορία ή κυνηγά τον επόμενο σύλλογο.",
      f1: "Παρουσίαση επιπέδου scouting",
      f2: "Επαγγελματική ροή μοντάζ",
      f3: "Ανάλυση με έμφαση στην απόδοση",
      f4: "Αποκλειστικά για ποδόσφαιρο",
    },
    services: {
      kicker: "— Υπηρεσίες",
      title: "Τι Κάνουμε",
      subtitle:
        "Δύο στοχευμένες υπηρεσίες. Ένας στόχος: να φτάσει η ιστορία σου στους κατάλληλους ανθρώπους.",
      price_label: "Επικοινωνία για τιμή",
      cta: "Ζήτησε Πληροφορίες",
      items: [
        {
          title: "Highlight Video",
          desc: "Επαγγελματικό ποδοσφαιρικό highlight video από υλικό αγώνων, εστιασμένο στις καλύτερες ενέργειες, στις βασικές στιγμές και στα δυνατά σημεία του παίκτη.",
          bullets: [
            "Καλύτερες ενέργειες & βασικές στιγμές",
            "Slow-motion στις καθοριστικές φάσεις",
            "Καθαρά cuts, branded intro",
          ],
        },
        {
          title: "Player Analysis",
          desc: "Λεπτομερής ατομική ανάλυση παίκτη βασισμένη σε υλικό αγώνων. Συνοδεύεται από γραπτή αναφορά και ατομική video ανάλυση — δυνατά σημεία, βασικές ενέργειες, τακτικά/τεχνικά στοιχεία και σημεία βελτίωσης.",
          bullets: [
            "Γραπτή scouting-style αναφορά",
            "Ατομική video ανάλυση",
            "Δυνατά σημεία, τακτική & σημεία βελτίωσης",
          ],
        },
      ],
    },
    portfolio: {
      kicker: "— Πρόσφατες Δουλειές",
      title: "Πρόσφατες Δουλειές",
      subtitle:
        "Highlight videos και ατομικές αναλύσεις παικτών για ποδοσφαιριστές σε όλες τις θέσεις και κατηγορίες.",
      cta: "Δες το Κανάλι YouTube",
      watch: "Δες",
      placeholder: "Δες στο YouTube",
      labels: { position: "Θέση", service: "Υπηρεσία" },
      videos: [
        { id: "5UumdwhJ_Cg", player: "Giannis Ntokos", position: "Δεξί Μπακ / Δεξί Wing-Back", service: "Highlight Video" },
        { id: "VnF3Tn7YGpo", player: "Konstantinos Tsaggaris", position: "Επιθετικός / Εξτρέμ / Επιθετικός Μέσος", service: "Highlight Video" },
        { id: "iz7xJ_20uSk", player: "Theodosis Prekas", position: "Αμυντικός Μέσος / Επιθετικός Μέσος", service: "Highlight Video" },
        { id: "EAvPkmncUjA", player: "Alexandros Charalampidis", position: "Επιθετικός Μέσος / Εξτρέμ", service: "Highlight Video" },
        { id: "qjY7zyI0XvQ", player: "Vasilis Patsatzoglou", position: "Στόπερ", service: "Highlight Video" },
        { id: "yzTBX1d1L3w", player: "Paris Prikas", position: "Αριστερό Μπακ / Αριστερό Wing-Back", service: "Highlight Video" },
        { id: "KnEFD4oyJrA", player: "Giannis Farmakis", position: "Στόπερ / Αμυντικός Μέσος", service: "Highlight Video" },
        { id: "3a5UPuNh7ig", player: "Aggelos Efthimiou", position: "Δεξί Μπακ / Αριστερό Μπακ", service: "Highlight Video" },
        { id: "H-nMOelFgvY", player: "Achilleas Pantelis", position: "Αριστερό Μπακ / Αριστερό Wing-Back / Αριστερό Εξτρέμ", service: "Highlight Video" },
        { id: "T3tzef-nT3w", player: "Salvatore Torma", position: "Στόπερ", service: "Highlight Video" },
        { id: "P5BNAt9fm_w", player: "Marios Ventouris", position: "Στόπερ", service: "Highlight Video" },
        { id: "f2gXsi0FFO8", player: "David Krupička", position: "Δεξί Μπακ / Αριστερό Μπακ", service: "Highlight Video" },
        { id: "4dtvAs9JFKI", player: "Luka Mamed-Ogly", position: "Αριστερό Εξτρέμ", service: "Highlight Video" },
        { id: "aPrS49Mh1Ck", player: "Konstantinos Kaipis", position: "Δεξί Μπακ / Αριστερό Μπακ", service: "Highlight Video" },
      ],
    },
    why: {
      kicker: "— Σε Ποιον Απευθύνεται",
      title_1: "Σε Ποιον Απευθύνεται",
      brand_a: "Player",
      brand_b: "Spotlight",
      subtitle:
        "Φτιαγμένο για τα δύο πρόσωπα που αποφασίζουν τι θα γίνει στη συνέχεια — τον παίκτη και τον ατζέντη.",
      audiences: [
        {
          tag: "Για Παίκτες",
          title: "Παρουσίασε το Παιχνίδι σου σαν Επαγγελματίας",
          desc: "Δείξε σε συλλόγους, προπονητές και scouts τι μπορείς πραγματικά — με υλικό που στέκει δίπλα στις μεγαλύτερες ακαδημίες.",
          bullets: [
            "Επαγγελματικά highlight videos που ξεχωρίζουν",
            "Ανάλυση τύπου scouting για τα δυνατά σου σημεία",
            "Υλικό έτοιμο για συλλόγους, scouts και ατζέντηδες",
            "Ξεχώρισε από χιλιάδες ακατέργαστα highlights",
          ],
        },
        {
          tag: "Για Ατζέντηδες",
          title: "Εξόπλισε Κάθε Πελάτη που Εκπροσωπείς",
          desc: "Κέρδισε ώρες μοντάζ και παρέδωσε παρουσιάσεις που ταιριάζουν με αυτό που πραγματικά περιμένουν να δουν οι σύλλογοι.",
          bullets: [
            "Έτοιμο υλικό για κάθε παίκτη του roster σου",
            "Αναφορές στη μορφή που διαβάζουν οι αποφασίζοντες",
            "Γρήγορη παράδοση για μεταγραφικές περιόδους",
            "Σταθερή ποιότητα σε όλους τους πελάτες σου",
          ],
        },
      ],
    },
    process: {
      kicker: "— Διαδικασία",
      title: "Πώς Δουλεύουμε",
      subtitle: "Μια απλή διαδικασία τεσσάρων βημάτων — από το υλικό σου στο τελικό παραδοτέο.",
      steps: [
        { title: "Στείλε το Υλικό σου", desc: "Μοιράσου τα βίντεο των αγώνων μέσω Drive, WeTransfer ή YouTube — ολόκληροι αγώνες ή κλιπ." },
        { title: "Επιλέγουμε & Αναλύουμε τις Βασικές Φάσεις", desc: "Βλέπουμε κάθε λεπτό και εντοπίζουμε τις στιγμές που αναδεικνύουν τα δυνατά σου." },
        { title: "Δημιουργούμε το Highlight Video ή την Player Analysis", desc: "Επαγγελματικό μοντάζ ή scouting-style ανάλυση, στα μέτρα της θέσης σου." },
        { title: "Λαμβάνεις Έτοιμο Υλικό", desc: "Παραδίδεται σε μορφές έτοιμες για συλλόγους, προπονητές, scouts και social media." },
      ],
    },
    contact: {
      kicker: "— Επικοινωνία",
      title_1: "Ζήτησε",
      title_2: "Πληροφορίες",
      subtitle:
        "Πες μας τι σε ενδιαφέρει και θα επικοινωνήσουμε μαζί σου με περισσότερες πληροφορίες και τιμές.",
      labels: {
        instagram: "Instagram",
        email: "Email",
        youtube: "YouTube",
        location: "Τοποθεσία",
      },
      location_value: "Παγκοσμίως · Remote",
      form: {
        name: "Όνομα",
        name_ph: "Το πλήρες όνομά σου",
        email: "Email",
        email_ph: "you@email.com",
        contact_handle: "Instagram / WhatsApp",
        contact_handle_ph: "@instagram ή νούμερο WhatsApp",
        position: "Θέση Παίκτη",
        position_ph: "π.χ. Κεντρικός Μέσος, Δεξί Μπακ, Τερματοφύλακας",
        interest: "Τι σε ενδιαφέρει;",
        interest_options: {
          highlight: "Highlight Video",
          analysis: "Player Analysis",
          both: "Και τα δύο",
        },
        message: "Μήνυμα / Σύνδεσμος υλικού",
        message_ph:
          "Πες μας λίγα λόγια για σένα και μοιράσου τον σύνδεσμο του υλικού σου (Drive, WeTransfer, YouTube).",
        submit: "Ζήτησε Πληροφορίες",
        submitting: "Αποστολή…",
        success: "Το αίτημα στάλθηκε! Θα επικοινωνήσουμε με λεπτομέρειες και τιμές.",
        error_required: "Συμπλήρωσε όνομα, email, τι σε ενδιαφέρει και μήνυμα.",
        error_generic: "Δεν έγινε δυνατή η αποστολή.",
      },
    },
    footer: {
      tagline: "Επαγγελματικά Football Highlight Videos & Player Analysis",
      rights: "Με επιφύλαξη παντός δικαιώματος",
      nav: { about: "Σχετικά", services: "Υπηρεσίες", portfolio: "Έργα", contact: "Επικοινωνία" },
    },
    lang_label: "Γλώσσα",
  },
};

const LanguageContext = createContext({ lang: "en", setLang: () => {}, t: translations.en });

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    try {
      return localStorage.getItem("ps_lang") || "en";
    } catch {
      return "en";
    }
  });

  const setLang = (l) => {
    setLangState(l);
    try {
      localStorage.setItem("ps_lang", l);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = { lang, setLang, t: translations[lang] || translations.en };
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useT = () => useContext(LanguageContext);
